import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { isDoubleArray } from '../util';
import { TermSelect } from '../css';

const flattenTerms = terms => (isDoubleArray(terms) ? _.flatten(terms) : terms);

/**
 * Renders the term presentation for a row or child device:
 * - more than one term option on a normal interactive row → @tds/core-select
 *   dropdown (options come from a flat list or a flattened double array)
 * - multiple term arrays for child devices → a single shared static label
 *   (same name for every device)
 * - a single term option → static label
 * - empty/malformed → nothing (safe fallback)
 */
const TermCell = ({
  terms,
  context,
  interactive,
  value,
  onChange,
  placeholder,
  name
}) => {
  if (!Array.isArray(terms) || terms.length === 0) return null;

  const isDouble = isDoubleArray(terms);
  const options = flattenTerms(terms);
  const optionCount = Array.isArray(options) ? options.length : 0;

  // Child devices with multiple term arrays → single shared label.
  if (context === 'child' && isDouble) {
    const sharedName = _.get(options, '0.fields.termName', '');
    return sharedName ? <span>{sharedName}</span> : null;
  }

  // Normal interactive row with more than one term option → dropdown.
  if (context === 'row' && interactive && optionCount > 1) {
    const selectOptions = options
      .map(term => ({
        text: _.get(term, 'fields.termName', ''),
        value: _.get(term, 'fields.termName', '')
      }))
      .filter(option => option.value);

    return (
      <TermSelect
        name={name}
        value={value || ''}
        onChange={event => onChange(event.target.value)}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {selectOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.text}
          </option>
        ))}
      </TermSelect>
    );
  }

  // Single term option (or non-interactive) → static label.
  const labelName = _.get(options, '0.fields.termName', '');
  return labelName ? <span>{labelName}</span> : null;
};

TermCell.propTypes = {
  terms: PropTypes.oneOfType([PropTypes.array]),
  context: PropTypes.oneOf(['row', 'child']),
  interactive: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  name: PropTypes.string
};

TermCell.defaultProps = {
  terms: [],
  context: 'row',
  interactive: false,
  value: '',
  onChange: () => {},
  placeholder: '',
  name: 'term'
};

export default TermCell;
